import { useEffect, useState } from "react";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { hasFormChanges } from "@/components/ui/dialogCloseGuard";
import { TextInput } from "@/components/ui/Field";
import { ApiError } from "@/lib/apiClient";
import {
  createGeschaeftspartnerObjektadresse,
  updateGeschaeftspartnerObjektadresse,
  type Objektadresse,
  type ObjektadresseIn,
} from "@/lib/kontakte";

interface AdresseForm {
  title: string;
  street: string;
  zipcode: string;
  city: string;
}

const EMPTY_FORM: AdresseForm = {
  title: "",
  street: "",
  zipcode: "",
  city: "",
};

function toForm(addr?: Objektadresse | null): AdresseForm {
  if (!addr) return { ...EMPTY_FORM };
  return {
    title: addr.title || "",
    street: addr.street || "",
    zipcode: addr.zipcode || "",
    city: addr.city || "",
  };
}

function formToPayload(form: AdresseForm, initial?: Objektadresse | null): ObjektadresseIn {
  const payload: ObjektadresseIn = {
    title: form.title.trim(),
    street: form.street.trim() || null,
    city: form.city.trim() || null,
    zipcode: form.zipcode.trim() || null,
  };
  if (initial != null && initial.ort_id == null && initial.address_id != null) {
    payload.address_id = initial.address_id;
  }
  return payload;
}

export interface ObjektadresseFormDialogProps {
  open: boolean;
  geschaeftspartnerId: number;
  initial?: Objektadresse | null;
  onClose: () => void;
  onSaved: (addr: Objektadresse) => void;
}

export function ObjektadresseFormDialog({
  open,
  geschaeftspartnerId,
  initial = null,
  onClose,
  onSaved,
}: ObjektadresseFormDialogProps) {
  const [form, setForm] = useState<AdresseForm>(EMPTY_FORM);
  const [formBaseline, setFormBaseline] = useState<AdresseForm>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const editOrtId = initial?.ort_id ?? null;

  useEffect(() => {
    if (!open) return;
    const next = toForm(initial);
    setForm(next);
    setFormBaseline(next);
    setFormError(null);
  }, [open, initial]);

  async function handleSave() {
    const payload = formToPayload(form, initial);
    if (!payload.title) {
      setFormError("Bitte eine Bezeichnung angeben.");
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const saved =
        editOrtId != null
          ? await updateGeschaeftspartnerObjektadresse(geschaeftspartnerId, editOrtId, payload)
          : await createGeschaeftspartnerObjektadresse(geschaeftspartnerId, payload);
      onSaved(saved);
      onClose();
    } catch (err) {
      setFormError(
        err instanceof ApiError ? err.message : "Objektadresse konnte nicht gespeichert werden.",
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={initial ? "Objektadresse bearbeiten" : "Objektadresse anlegen"}
      dirty={hasFormChanges(form, formBaseline)}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button onClick={() => void handleSave()} disabled={saving}>
            {saving ? "Speichern…" : "Speichern"}
          </Button>
        </>
      }
    >
      {formError ? (
        <Alert variant="error" title="Eingabe" className="mb-4">
          {formError}
        </Alert>
      ) : null}
      <div className="grid gap-4 sm:grid-cols-2">
        <TextInput
          label="Bezeichnung"
          className="sm:col-span-2"
          value={form.title}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          placeholder="z. B. Verwaltungsgebäude, Schule Nord"
        />
        <TextInput
          label="Straße"
          className="sm:col-span-2"
          value={form.street}
          onChange={(e) => setForm((f) => ({ ...f, street: e.target.value }))}
        />
        <TextInput
          label="PLZ"
          value={form.zipcode}
          onChange={(e) => setForm((f) => ({ ...f, zipcode: e.target.value }))}
        />
        <TextInput
          label="Ort"
          value={form.city}
          onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
        />
      </div>
    </Dialog>
  );
}
